import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { useFormContext } from 'react-hook-form';

import { AnalyticsContext } from '../analytics-dashboard';
import { getSurveyQuestionSets } from 'redux/selectors/analytics';
import { useFetchSurveyQuestions, useSyncDropdownValue, questionsToOptions } from './utils';

import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import Divider from 'shared/components/divider';

// Sub-component for NPS/CSAT section
interface MetricSelectionSectionProps {
  title: string;
  surveyFieldName: string;
  questionFieldName: string;
  surveyOptions: NvDropdownOption[];
  questionOptions: NvDropdownOption[];
  onSurveyChange: (value: NvDropdownOption) => void;
  onQuestionChange: (value: NvDropdownOption) => void;
  className?: string;
}

const MetricSelectionSection = ({
  title,
  surveyFieldName,
  questionFieldName,
  surveyOptions,
  questionOptions,
  onSurveyChange,
  onQuestionChange,
  className = '',
}: MetricSelectionSectionProps) => (
  <div className={className}>
    <div className='bold'>{title}</div>
    <div className='d-flex'>
      <div className='flex-grow-1 text-regular'>
        <NvFormDropdown
          items={surveyOptions}
          name={surveyFieldName}
          className='my-2'
          onChange={onSurveyChange}
          title={t.COURSE_ANALYTICS.PRE_POST.MODAL.SURVEY_SELECT_PLACEHOLDER()}
        />
      </div>
      <div className='flex-grow-1 text-regular ml-5'>
        <NvFormDropdown
          items={questionOptions}
          name={questionFieldName}
          className='my-2'
          onChange={onQuestionChange}
          title={t.COURSE_ANALYTICS.PRE_POST.MODAL.SURVEY_QUESTION_SELECT_PLACEHOLDER()}
        />
      </div>
    </div>
  </div>
);

interface NpsCsatSelectionProps {
  preCatalogId: string;
}

const NpsCsatSelection = ({ preCatalogId }: NpsCsatSelectionProps) => {
  const { watch, setValue } = useFormContext();
  const { analyticsPageType } = useContext(AnalyticsContext);
  const allSurveys = useSelector((state) =>
    getSurveyQuestionSets(state, analyticsPageType!, preCatalogId),
  );

  // Watch form values
  const npsSurveyId = watch('npsSurveyId');
  const npsQuestionId = watch('npsQuestionId');
  const csatSurveyId = watch('csatSurveyId');
  const csatQuestionId = watch('csatQuestionId');

  // Fetch questions for NPS and CSAT
  const npsQuestions = useFetchSurveyQuestions(npsSurveyId, preCatalogId, 'metricEligibleForNps');
  const csatQuestions = useFetchSurveyQuestions(csatSurveyId, preCatalogId, 'metricEligibleForCsat');

  // Transform to dropdown options
  const surveyOptions = useMemo(
    () =>
      allSurveys?.map((survey) => ({
        type: 'text',
        id: survey.id,
        value: survey.id,
        text: survey.title,
        class: 'text-wrap',
      })) || [],
    [allSurveys],
  );

  const npsQuestionOptions = useMemo(() => questionsToOptions(npsQuestions), [npsQuestions]);
  const csatQuestionOptions = useMemo(() => questionsToOptions(csatQuestions), [csatQuestions]);

  // Sync dropdown values
  useSyncDropdownValue(npsSurveyId, 'npsSurveyDropdown', surveyOptions);
  useSyncDropdownValue(npsQuestionId, 'npsQuestionDropdown', npsQuestionOptions);
  useSyncDropdownValue(csatSurveyId, 'csatSurveyDropdown', surveyOptions);
  useSyncDropdownValue(csatQuestionId, 'csatQuestionDropdown', csatQuestionOptions);

  // Handlers
  const handleNpsSurveyChange = (value: NvDropdownOption) => {
    setValue('npsSurveyId', value.id);
    setValue('npsQuestionId', null);
    setValue('npsQuestionDropdown', undefined);
  };

  const handleNpsQuestionChange = (value: NvDropdownOption) => {
    setValue('npsQuestionId', value.id);
  };

  const handleCsatSurveyChange = (value: NvDropdownOption) => {
    setValue('csatSurveyId', value.id);
    setValue('csatQuestionId', null);
    setValue('csatQuestionDropdown', undefined);
  };

  const handleCsatQuestionChange = (value: NvDropdownOption) => {
    setValue('csatQuestionId', value.id);
  };

  return (
    <div>
      <div className='mt-5 mb-5'>
        <Divider />
      </div>
      <div className='mb-4 font-weight-bolder'>
        {t.COURSE_ANALYTICS.PRE_POST.MODAL.SURVEY_QUESTIONS()}
      </div>
      <MetricSelectionSection
        title={t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.NPS_QUESTION()}
        surveyFieldName='npsSurveyDropdown'
        questionFieldName='npsQuestionDropdown'
        surveyOptions={surveyOptions}
        questionOptions={npsQuestionOptions}
        onSurveyChange={handleNpsSurveyChange}
        onQuestionChange={handleNpsQuestionChange}
      />
      <MetricSelectionSection
        title={t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.CSAT_QUESTION()}
        surveyFieldName='csatSurveyDropdown'
        questionFieldName='csatQuestionDropdown'
        surveyOptions={surveyOptions}
        questionOptions={csatQuestionOptions}
        onSurveyChange={handleCsatSurveyChange}
        onQuestionChange={handleCsatQuestionChange}
        className='mt-4'
      />
    </div>
  );
};

export default NpsCsatSelection;
